import { useMemo } from 'react';
import { toast } from 'sonner';

import { TanStackTable } from '@/components/table/TanStackTable';
import { useOpenEntityDrawer } from '@/hooks/useOpenEntityDrawer';
import { buildColumnsForEntity } from '@/sections/insights/queryBuilder/entities';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';
import { useAuth } from '@/state/auth';
import type { GraphQueryResponse } from '@/types';

import { extractQueryResults } from './utils';

interface TableResultsProps {
  data: GraphQueryResponse;
}

/**
 * TableResults - Displays query results in a table with entity-specific columns
 *
 * Automatically detects entity type from GraphQueryResponse and
 * renders appropriate columns using buildColumnsForEntity.
 *
 * @example
 * // For asset data, displays Group and Identifier columns
 * <TableResults data={{ assets: [...], count: 2 }} />
 *
 * @example
 * // For risk data, falls back to Key column
 * <TableResults data={{ risks: [...], count: 2 }} />
 */
export const TableResults = ({ data }: TableResultsProps) => {
  const { isGlobal, usernames } = useQueryBuilder();
  const { isPraetorianUser } = useAuth();
  const { openDrawerByKey } = useOpenEntityDrawer();

  // Extract data array and entity type
  const results = useMemo(() => extractQueryResults(data), [data]);

  // Build columns based on detected entity type
  const columns = useMemo(
    () =>
      results
        ? buildColumnsForEntity(
            results.type,
            isGlobal,
            usernames,
            isPraetorianUser
          )
        : [],
    [results, isGlobal, usernames, isPraetorianUser]
  );

  // Extract data array for table
  const tableData = useMemo(() => results?.data ?? [], [results]);

  const handleRowClick = (row: unknown) => {
    const rowData = row as Record<string, unknown>;

    if (isGlobal) {
      toast.error(
        'Table rows are not clickable in global query mode. Please assume into a specific account to review full item details.'
      );
      return;
    }

    if (!rowData.key) {
      toast.error('Unable to open details: missing entity key');
      return;
    }

    var key = String(rowData.key);
    if (key && key.startsWith('#port')) {
      key = String(rowData.source);
    } else if (key && key.startsWith('#webpage')) {
      key = '#webapplication#' + String(rowData.url);
    }

    // Use the key-based drawer opening (auto-detects entity type from key prefix)
    openDrawerByKey(key);
  };

  return (
    <div data-testid="query-table-view" className="h-full">
      <p className="pb-4">
        <i>Fetched {tableData.length} results.</i>
      </p>
      <TanStackTable
        name="query-results"
        columns={columns}
        data={tableData}
        status="success"
        error={null}
        className="h-full"
        onRowClick={handleRowClick}
        rowClassName={row => {
          const rowData = row as Record<string, unknown>;
          return rowData.key
            ? 'cursor-pointer transition-all hover:bg-chariot-background-secondary'
            : undefined;
        }}
      />
    </div>
  );
};
